// Central Brain Overview view.
// Sprint 2 PR-D1: raw fetch → apiGetJson (401 redirect + 5xx toast + typed
// guard). Mevcut 12 card render davranışı korunur; loading / error state
// iyileştirildi; pure renderOverviewHtml() helper test edilebilir.

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

export interface OverviewCards {
  eventsToday: number;
  customerOps: number;
  internalOps: number;
  pendingApprovals: number;
  autoCompleted24h: number;
  riskyActions: number;
  runningAgents: number;
  recentCouncilDecisions: unknown[];
  wikiFreshness: { total: number; byLevel: Record<string, number>; staleProjects: unknown[] };
  tokenBudgetWarnings: unknown[];
  whatsappSimulator: { runs24h: number };
  staleProjects: number;
  skillUpdateProposals: number;
}

interface OverviewResponse {
  ok: true;
  timestamp: string;
  cards: OverviewCards;
}

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; cards: OverviewCards }
  | { kind: 'error'; message: string };

let state: ViewState = { kind: 'idle' };

export async function loadCentralBrain(): Promise<void> {
  state = { kind: 'loading' };
  const result = await apiGetJson<OverviewResponse>('/api/v2/central-brain/overview');
  if (result.ok) {
    state = { kind: 'ready', cards: result.data.cards };
  } else {
    state = { kind: 'error', message: result.message ?? result.error };
  }
}

export function renderCentralBrain(): void {
  const root = document.getElementById('view-central-brain');
  if (!root) return;
  root.innerHTML = renderOverviewHtml(state);
}

// Pure render — test edilebilir. State'i alır, HTML string döner.
export function renderOverviewHtml(s: ViewState): string {
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">Central Brain</div>
        <div class="view-sub">Castintech ekosisteminin merkezi AI operasyon beyni — son 24 saat özet</div>
      </div>
    </div>`;
  if (s.kind === 'idle' || s.kind === 'loading') {
    return `${header}<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor…</div></div>`;
  }
  if (s.kind === 'error') {
    return `${header}<div class="rt-card" style="border-left:3px solid var(--danger,#e55);"><div class="rt-card-title">Yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(s.message)}</div></div>`;
  }
  const c = s.cards;
  return `
    ${header}
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px;">
      ${card('Bugünkü event', c.eventsToday)}
      ${card('Müşteri ops', c.customerOps, true)}
      ${card('İç ops', c.internalOps, true)}
      ${card('Bekleyen onay', c.pendingApprovals)}
      ${card('24h otomatik', c.autoCompleted24h, true)}
      ${card('Riskli aksiyon', c.riskyActions)}
      ${card('Çalışan ajan', c.runningAgents)}
      ${card('Stale proje', c.staleProjects)}
      ${card('Skill update', c.skillUpdateProposals, true)}
      ${card('WhatsApp simulator (24h)', c.whatsappSimulator.runs24h)}
      ${card('Wiki freshness (toplam)', c.wikiFreshness.total)}
      ${card('Wiki kritik', c.wikiFreshness.byLevel.critical ?? 0)}
    </div>
  `;
}

// notWired: backend overview'da Sprint-1 stub olan alanlar (routes-v2
// customerOps/internalOps/autoCompleted24h/skillUpdateProposals sabit 0 döner).
// Gerçek sıfırla karışıp yanlış güven vermesin diye "—  · bağlı değil" gösterilir;
// backend gerçek kaynağa bağlanınca ilgili karttan üçüncü argüman kaldırılır.
function card(title: string, value: number | string, notWired = false): string {
  const val = notWired ? '—' : escHtml(String(value));
  const sub = notWired
    ? '<div style="font-size:10px;color:var(--muted);margin-top:2px;">bağlı değil (Sprint stub)</div>'
    : '';
  return `<div class="rt-card" style="padding:14px;">
    <div style="font-size:11px;color:var(--muted);text-transform:uppercase;">${escHtml(title)}</div>
    <div style="font-size:24px;font-weight:600;margin-top:6px;">${val}</div>${sub}
  </div>`;
}
